import * as React from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Switch, Route } from 'react-router';
import { RoutingConstants } from '../Constant';
import { Chat } from './Chat/Chat';
import { ErrorMessage } from './ErrorMessage/ErrorMessage';
import { useAppStyles } from '../App.styles';
import { mergeClasses } from '@fluentui/react-components';
import {
  CareerHubModuleReactContext,
  appHeaderHeight,
  careerStateSelector,
  copilotPanelWidth,
  copilotPanelWidthOnSmallScreen,
  mediumScreenWidth,
  loadTaxonomyType,
  loadHrProfile,
} from '@careerhub/common';
import { useDispatch, useSelector } from 'react-redux';
import RoleLibraryPage from '../pages/RoleLibrary/RoleLibraryPage';
import { LevelThreePage } from '../pages/Role/LevelThreePage';
import { LevelTwoPage } from '../pages/Discipline/LevelTwoPage';
import { Footer } from './Footer/Footer';
import { NotificationBanner } from './NotificationBanner/NotificationBanner';
import { MessageBarType } from 'office-ui-fabric-react';
import { Notification } from '@careerhub/api';
import { Header } from './Header/Header';
import { FrameContexts } from '@microsoft/teams-js';

interface IMainContentProps {
  showHeader: boolean;
  frameContext: FrameContexts | undefined;
}

/** The main page and copilot panel for the Career Hub */
// Needs to be in its own component to conditionally change margin based on copilot panel being open or closed
export const MainContent: React.FC<IMainContentProps> = (props) => {
  const { showHeader, frameContext } = props;
  // TODO separate app styles from main content styles
  const styles = useAppStyles();
  // TODO: Avoid this app height enable and disable.
  const contentStyle = showHeader ? mergeClasses(styles.content, styles.contentHeight) : styles.content;
  const chatPanelStyle = showHeader ? mergeClasses(styles.chatPanel, styles.contentHeight) : styles.chatPanel;
  const { isChatOpen, taxonomyTypes } = useSelector(careerStateSelector);
  const { careerService } = React.useContext(CareerHubModuleReactContext);
  const [isMediumOrSmallerScreen, setIsMediumOrSmaller] = React.useState(window.innerWidth < 640);
  const [notifications, setNotificatons] = React.useState<Notification[]>();
  const dispatch = useDispatch();

  React.useEffect(() => {
    careerService.getCareerServiceConfiguration().then((response) => {
      setNotificatons(response.notifications);
    });
  }, [careerService]);

  React.useEffect(() => {
    function handleResize() {
      setIsMediumOrSmaller(window.innerWidth < mediumScreenWidth);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    dispatch(loadTaxonomyType());
    dispatch(loadHrProfile());
  }, [dispatch]);

  const renderNotifications = () => {
    if (!notifications?.length) return null;
    return (
      <>
        {notifications.map((element) => (
          <div>
            <NotificationBanner
              messageType={getMessageBarType(element.severity)}
              message={element.message || ''}
              linkHref={element.linkHref || ''}
              linkText={element.linkText || ''}
              StartDate={element.startDateUtc}
              EndDate={element.endDateUtc}
            />
          </div>
        ))}
      </>
    );
  };

  const severityMapping: Record<string, MessageBarType> = {
    blocked: MessageBarType.blocked,
    error: MessageBarType.error,
    info: MessageBarType.info,
    severewarning: MessageBarType.severeWarning,
    success: MessageBarType.success,
    warning: MessageBarType.warning,
  };

  function getMessageBarType(messageSeverity: any): MessageBarType {
    const severity: string = messageSeverity || '';
    return severityMapping[severity.toLowerCase().trim()] || MessageBarType.info;
  }
  const level2Type = taxonomyTypes.find((item) => item.level === 2)?.id || 'Discipline';
  const level3Type = taxonomyTypes.find((item) => item.level === 3)?.id || 'Role';

  const isMainContentRendered = !isMediumOrSmallerScreen || isChatOpen === false;

  const handleSkipToMainContent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Skip to main content link: first focusable element, bypasses repeated navigation (WCAG 2.4.1). */}
      {/* Only render when its target (#main-content) is present. */}
      {isMainContentRendered && (
        <a href="#main-content" className={styles.skipLink} onClick={handleSkipToMainContent}>
          Skip to main content
        </a>
      )}
      {/* App header. Don't show if chat is open and screen is medium or smaller */}
      {showHeader && !(isMediumOrSmallerScreen && isChatOpen) && (
        <div className={styles.header}>
          <Header />
        </div>
      )}
      {isMainContentRendered && (
        <div
          className={frameContext == FrameContexts.content ? contentStyle : styles.appTaskModuleContainer}
          style={{
            marginRight: isChatOpen ? (screen.width > 320 ? copilotPanelWidth : copilotPanelWidthOnSmallScreen) : '0px',
          }}
        >
          {renderNotifications()}
          <OverlayScrollbarsComponent
            style={{ maxHeight: `calc(100vh - ${showHeader ? appHeaderHeight : '0px'})` }}
            options={{ overflow: { x: 'hidden', y: 'scroll' }, scrollbars: { visibility: 'auto' } }}
            events={{
              initialized: (instance) => {
                const { scrollbarHorizontal, scrollbarVertical } = instance.elements();
                scrollbarHorizontal.scrollbar.setAttribute('aria-hidden', 'true');
                scrollbarVertical.scrollbar.setAttribute('aria-hidden', 'true');
              },
            }}
          >
            <main id="main-content" tabIndex={-1}>
              <ErrorMessage />
              <Switch>
                <Route path={RoutingConstants.HomeRoute} component={RoleLibraryPage} exact={true} />
                <Route path={RoutingConstants.RoleLibrary} component={RoleLibraryPage} exact={true} />
                <Route exact path={`/${level3Type}/:levelThreeId`} component={LevelThreePage} />
                <Route path={`/${level2Type}/:levelTwoId`} component={LevelTwoPage} exact />
              </Switch>
            </main>
            <Footer />
          </OverlayScrollbarsComponent>
        </div>
      )}
      <div className={chatPanelStyle}>{frameContext == FrameContexts.content && <Chat />}</div>
    </>
  );
};
